import * as React from "react";
import { AlertTriangle, LucideIcon } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Main heading for the error. Defaults to “Something went wrong”. */
  title?: string;
  /** Detailed message describing the error. */
  message: string;
  /** Show the retry action button. Defaults to true. */
  showRetry?: boolean;
  /** Callback when retry is clicked. */
  onRetry?: () => void;
  /** Optional custom icon. Defaults to AlertTriangle. */
  icon?: LucideIcon;
  /** Label for the retry button. */
  retryLabel?: string;
}

export function ErrorState({
  title = "Something went wrong",
  message,
  showRetry = true,
  onRetry,
  icon: Icon = AlertTriangle,
  retryLabel = "Retry",
  className,
  ...props
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-[220px] flex-col items-center justify-center text-center space-y-3",
        className
      )}
      {...props}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <Icon className="h-6 w-6" aria-hidden />
      </div>
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-gray-500 max-w-md">{message}</p>
      </div>
      {showRetry && (
        <Button variant="destructive" onClick={onRetry} className="mt-2">
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
